'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';

import {
    Align,
    Box,
    BoxHeader,
    Button,
    Icon,
    IconName,
    IconSize,
    Justify,
    List,
    ListItem,
    Modal,
    ModalBody,
    ModalFooter,
    Price,
    PriceLevel,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    View,
} from '@bytel/trilogy-react';

import { MobilePlanType } from '@app-types/plan';

import { SSIBlock } from '@components/ssi-block';

import { partials } from '@helpers/partials';
import { formatTagText } from '@helpers/tracking';

import { JourneyConfigType, sendTrackingFromEligibilityMismatchError } from '@eligibility/helpers';

export function ByouPlanCard({
    mobilePlan,
    journeyConfig,
}: {
    mobilePlan: MobilePlanType;
    journeyConfig: JourneyConfigType;
}) {
    const [shouldShowEuropeModal, setShouldShowEuropeModal] = useState(false);
    const router = useRouter();
    const features = [
        {
            icon: IconName.AT,
            text: (
                <Text level={TextLevels.THREE}>
                    {mobilePlan.dataEnvelope} d&apos;internet
                    {mobilePlan.roamingEnvelope ? ` dont ${mobilePlan.roamingEnvelope} utilisables en Europe` : null}
                </Text>
            ),
        },
        {
            icon: IconName.PHONE_INFINITY,
            text: <Text level={TextLevels.THREE}>{mobilePlan.smsMms}</Text>,
        },
        {
            icon: IconName.SHOOTING_STAR,
            text: (
                <Text level={TextLevels.THREE}>
                    La qualité du réseau {mobilePlan.maxConnections === '5g' ? '5G' : '4G'} de Bouygues Telecom
                </Text>
            ),
        },
    ];

    return (
        <>
            <Box className='byou-card-custom is-paddingless'>
                <BoxHeader className='byou-card-custom-header has-background-info-fade is-marginless'>
                    <View flexable align={Align.CENTER} justify={Justify.SPACE_BETWEEN}>
                        <Title marginless level={TitleLevels.FOUR}>
                            Forfait B&YOU&nbsp;{mobilePlan.dataEnvelope.split(' ').join(' ')}
                        </Title>
                        <Icon
                            size={IconSize.MEDIUM}
                            name={mobilePlan.maxConnections === '4g' ? IconName.UI_4G : IconName.UI_5G}
                            stretched
                        />
                    </View>
                </BoxHeader>
                <View className='card-body'>
                    <List className='icon-list' ordered={false}>
                        {features.map((feature) => (
                            <ListItem key={feature.text.key}>
                                <View className='icon-and-text'>
                                    <Icon size={IconSize.SMALLER} name={feature.icon} />
                                    {feature.text}
                                </View>
                            </ListItem>
                        ))}
                    </List>
                    <View flexable className='is-aligned-center is-vertical'>
                        <Price amount={mobilePlan.details.price.final} level={PriceLevel.FIVE} period='mois' />
                        <Text level={TextLevels.TWO}>Sans engagement</Text>
                    </View>
                    <Spacer size={SpacerSize.THREE} />

                    <Button
                        className='byou-bg has-text-white is-fullwidth is-small has-byou-background'
                        onClick={() => {
                            sendTrackingFromEligibilityMismatchError(
                                journeyConfig,
                                `Continuer_avec_ce_forfait_${formatTagText(mobilePlan.name)}`,
                            );
                            router.push('/forfaits-mobiles/sans-engagement/panier');
                        }}
                    >
                        Continuer avec ce forfait
                    </Button>
                    <Spacer size={SpacerSize.FOUR} />
                    <View flexable className='is-centered'>
                        <button
                            className='link button-clear has-text-centered'
                            onClick={() => setShouldShowEuropeModal(true)}
                        >
                            * Voir les destinations en Europe
                        </button>
                    </View>
                </View>
            </Box>
            <Modal
                active={shouldShowEuropeModal}
                onClose={() => setShouldShowEuropeModal(false)}
                title='Destinations en Europe'
            >
                <ModalBody>
                    <SSIBlock url={partials.MARKETING.PLAN_EUROPE} />
                </ModalBody>
                <ModalFooter>
                    <Button className='byou-bg' onClick={() => setShouldShowEuropeModal(false)}>
                        Fermer
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
